import { useState, useRef, useEffect } from 'react';

export interface EditableDateProps {
  /** Current value in YYYY-MM-DD format, or undefined/null for empty */
  value: string | undefined | null;
  /** Called when date is saved - can be async. Value is YYYY-MM-DD or null if cleared */
  onSave: (value: string | null) => void | Promise<void>;
  /** Whether editing is disabled */
  disabled?: boolean;
  /** Width of the editable field (Tailwind class, e.g., 'w-32', 'w-40') */
  width?: string;
  /** Custom class for additional styling */
  className?: string;
  /** Placeholder text when no date is set */
  placeholder?: string;
  /** Original value for reset functionality - if different from value, shows reset button */
  originalValue?: string | null;
  /** Called when reset button clicked */
  onReset?: () => void | Promise<void>;
}

// Inline SVG icon for undo/reset
function UndoIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 10h11a5 5 0 010 10h-1M3 10l4-4m-4 4l4 4"
      />
    </svg>
  );
}

export function EditableDate({
  value,
  onSave,
  disabled = false,
  width = 'w-32',
  className = '',
  placeholder = '-',
  originalValue,
  onReset,
}: EditableDateProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      // Open the date picker on focus (works in most browsers)
      inputRef.current.showPicker?.();
    }
  }, [isEditing]);

  const startEditing = () => {
    if (disabled || isSaving) return;
    setInputValue(value || '');
    setIsEditing(true);
  };

  const save = async () => {
    // Only save if value changed
    const newValue = inputValue || null;
    const oldValue = value || null;
    if (newValue !== oldValue) {
      setIsSaving(true);
      try {
        await onSave(newValue);
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 2000);
      } catch {
        setShowError(true);
        setTimeout(() => setShowError(false), 2000);
      } finally {
        setIsSaving(false);
      }
    }
    setIsEditing(false);
  };

  const cancel = () => {
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      save();
    } else if (e.key === 'Escape') {
      cancel();
    }
  };

  // Action button handler
  const handleReset = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onReset) {
      setIsSaving(true);
      try {
        await onReset();
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 2000);
      } catch {
        setShowError(true);
        setTimeout(() => setShowError(false), 2000);
      } finally {
        setIsSaving(false);
      }
    }
  };

  // Determine if reset button should be shown
  const showReset =
    onReset && originalValue !== undefined && (originalValue || null) !== (value || null);
  const hasActions = showReset;
  const showActions = isHovering && !isEditing && !isSaving && hasActions;

  // Shared styles for consistent sizing between button and input
  const baseStyles = `${width} py-1.5 px-2 text-sm font-medium text-gray-700 rounded-md border text-right transition-all duration-200`;

  // Success/error state: solid border
  const statusStyles = showSuccess ? 'border-primary-500' : showError ? 'border-red-500' : '';

  // Saving state: pulsing glow
  const savingStyles = isSaving
    ? 'border-primary-500 shadow-[0_0_0_3px_rgba(44,160,28,0.4)] animate-pulse'
    : '';

  const displayValue = value || placeholder;
  const hasStatus = showSuccess || showError || isSaving;
  // Use isHovering state for border instead of CSS hover so it stays active when hovering action buttons
  const borderStyle = hasStatus ? '' : isHovering ? 'border-gray-300' : 'border-transparent';

  // Action buttons are positioned absolutely above the element.
  // We add a tiny delay to onMouseLeave to prevent flickering if there's a 1px gap or fast movement.

  const hoverTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = undefined;
    }
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHovering(false);
    }, 100);
  };

  return (
    <div
      className={`${width} relative`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Action buttons - positioned above the input */}
      {showActions && (
        <div className="absolute bottom-full right-0 mb-1 z-10">
          <div className="flex gap-0.5 p-1 bg-white rounded-lg shadow-lg border border-gray-200">
            <button
              type="button"
              onClick={handleReset}
              className="group/reset relative p-1 rounded hover:bg-gray-100 transition-colors"
              title="Reset to original"
            >
              <UndoIcon className="w-3.5 h-3.5 text-gray-500" />
            </button>
          </div>
          {/* Arrow pointing down */}
          <div className="absolute top-full right-2 w-0 h-0 border-x-[6px] border-x-transparent border-t-[6px] border-t-white" />
          <div className="absolute top-full right-2 mt-px w-0 h-0 border-x-[6px] border-x-transparent border-t-[6px] border-t-gray-200 -z-10" />
        </div>
      )}
      {/* Inner container for button/input */}
      <div className="relative">
        {isEditing ? (
          <input
            ref={inputRef}
            type="date"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onBlur={save}
            onKeyDown={handleKeyDown}
            className={`${baseStyles} border-gray-300 bg-white outline-none focus:border-gray-400 ${savingStyles} ${statusStyles} ${className}`}
          />
        ) : (
          <button
            type="button"
            onClick={startEditing}
            disabled={disabled || isSaving}
            className={`${baseStyles} ${borderStyle} focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed ${savingStyles} ${statusStyles} ${className}`}
          >
            {displayValue}
          </button>
        )}
      </div>
    </div>
  );
}
